import { useEffect, useState } from 'react';
import { MIN_CHANGE_JUSTIFICATION } from '@obraplan/core/domain/planning/planning-change';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';

/**
 * Mover uma alocação para outro dia.
 *
 * Um campo de data, e não arrastar e soltar. A folha do dia é lida em tablet, onde
 * arrastar entre dias que não estão na mesma tela é gesto sem alvo — e o teclado do
 * canteiro é o dedo. Quem quer arrastar tem a grade da semana, onde os sete dias estão
 * visíveis lado a lado.
 *
 * A tela não decide se cabe: quem verifica a jornada do destino é o caso de uso (RN-044),
 * e a recusa volta escrita, com a capacidade e o excedente.
 *
 * Mover é alterar, e entra no histórico (RN-293). O motivo só é exigido se o dia de destino
 * passar da jornada (RN-292) — e sem o campo a recusa do caso de uso não teria onde ser
 * respondida e a linha ficaria impossível de mover para um dia cheio.
 */
export interface MoveAllocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  currentDate: string;
  isPending: boolean;
  errorMessage?: string;
  onConfirm: (input: { date: string; justification?: string }) => void;
}

export function MoveAllocationDialog({
  open,
  onOpenChange,
  resourceName,
  currentDate,
  isPending,
  errorMessage,
  onConfirm,
}: MoveAllocationDialogProps) {
  const [date, setDate] = useState(currentDate);
  const [justification, setJustification] = useState('');

  useEffect(() => {
    if (open) {
      setDate(currentDate);
      setJustification('');
    }
  }, [open, currentDate]);

  const trimmed = justification.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mover para outro dia</DialogTitle>
          <DialogDescription>
            As horas de {resourceName} saem deste dia e entram no escolhido. Se o dia de destino já
            tiver a mesma atividade, as horas somam na que está lá.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Field
            label="Novo dia"
            htmlFor="move-date"
            required
            hint="Pode cair em outra semana — o planejamento dela é criado se ainda não existir."
          >
            <Input
              id="move-date"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>

          <Field
            label="Motivo"
            htmlFor="move-reason"
            hint={`Só é exigido se as horas passarem da jornada do dia de destino, com pelo menos ${String(MIN_CHANGE_JUSTIFICATION)} caracteres.`}
            {...(errorMessage === undefined ? {} : { error: errorMessage })}
          >
            <Textarea
              id="move-reason"
              rows={2}
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              placeholder="Ex.: chuva na quinta; serviço passou para sexta."
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isPending || date === '' || date === currentDate}
            onClick={() =>
              onConfirm({ date, ...(trimmed === '' ? {} : { justification: trimmed }) })
            }
          >
            {isPending ? 'Movendo…' : 'Mover'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
